import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import {
  actionLinkClassName,
  AttentionList,
  AttentionSummary,
  Tile,
} from '../../../components/common/detailChrome';
import {
  Chip,
  EmptyState,
  Flag,
} from '../../../components/common/detailListParts';
import {
  CopyableValue,
  Field,
  Section,
} from '../../../components/common/detailParts';
import { TypedStatusBadge } from '../../../components/common/StatusBadge';
import { formatRelativeTime } from '../../../lib/formatters';
import {
  PublisherDappRow,
  sortDappsByActivity,
} from '../components/PublisherDappRow';
import {
  deriveAttention,
  explorerUrl,
  publisherWebsite,
  sumsubApplicantUrl,
} from '../publisherSignals';
import type {
  SuperPublisherDetailData,
  SuperPublisherTabProps,
} from '../types';

const LINK_CLASS = 'text-teal-400 transition-colors hover:text-teal-300';
const RECENT_DAPPS = 5;

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/**
 * Left column: the publisher's standing and its apps. Right rail: who they
 * are, the account behind them, and where they live on-chain.
 */
export function OverviewTab(props: SuperPublisherTabProps) {
  const { data, canManagePublisher, formatDate, onTabChange, onEditProfile } =
    props;
  const attention = deriveAttention(data, { isAdmin: canManagePublisher });

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
      <div className="min-w-0 space-y-4">
        <StandingCard
          data={data}
          attention={attention}
          canManagePublisher={canManagePublisher}
          onTabChange={onTabChange}
        />
        <RecentDappsCard dapps={data.dapps} onTabChange={onTabChange} />
      </div>
      <aside className="min-w-0 space-y-4">
        <ProfileCard
          publisher={data.publisher}
          formatDate={formatDate}
          canManagePublisher={canManagePublisher}
          onEditProfile={onEditProfile}
        />
        {canManagePublisher && (
          <AccountCard user={data.publisher.user} formatDate={formatDate} />
        )}
        <OnChainCard publisher={data.publisher} />
      </aside>
    </div>
  );
}

function StandingCard({
  data,
  attention,
  canManagePublisher,
  onTabChange,
}: {
  data: SuperPublisherDetailData;
  attention: ReturnType<typeof deriveAttention>;
  canManagePublisher: boolean;
  onTabChange: SuperPublisherTabProps['onTabChange'];
}) {
  const { publisher, stats } = data;
  const kyb = publisher.kybSubmission;
  const verificationNote = publisher.kycKybBypassEnabled
    ? 'Bypass enabled'
    : kyb?.reviewCompletedAt
      ? `Reviewed ${formatRelativeTime(kyb.reviewCompletedAt)}`
      : kyb?.submittedAt
        ? `Submitted ${formatRelativeTime(kyb.submittedAt)}`
        : 'No submission yet';

  return (
    <Section
      title="Standing"
      trailing={<AttentionSummary count={attention.length} />}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Tile label="Verification">
          <div>
            {kyb ? (
              <TypedStatusBadge type="kyb" status={kyb.status} />
            ) : (
              <span className="text-sm text-neutral-500">Not started</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 text-xs text-neutral-500">
            <span>{verificationNote}</span>
            {canManagePublisher && kyb?.sumsubApplicantId && (
              <a
                href={sumsubApplicantUrl(kyb.sumsubApplicantId)}
                target="_blank"
                rel="noopener noreferrer"
                className={`${LINK_CLASS} inline-flex items-center gap-1`}
              >
                Sumsub
                <OpenInNewIcon className="size-3 fill-current" />
              </a>
            )}
          </div>
        </Tile>

        <Tile label="Account">
          <Flag on={!publisher.isFrozen}>
            {publisher.isFrozen ? 'Frozen' : 'Active'}
          </Flag>
          <div className="truncate text-xs text-neutral-500">
            {publisher.role}
            {canManagePublisher && publisher.isOwner ? ' · owner' : ''}
            {publisher.kycKybBypassEnabled ? ' · KYC/B bypass' : ''}
          </div>
        </Tile>

        <Tile label="Portfolio">
          <div className="text-sm font-medium tabular-nums text-white">
            {stats.totalDapps} {stats.totalDapps === 1 ? 'dapp' : 'dapps'}
          </div>
          <div className="truncate text-xs tabular-nums text-neutral-500">
            {stats.publishedDapps} live · {stats.underReviewDapps} in review ·{' '}
            {stats.totalReleases}{' '}
            {stats.totalReleases === 1 ? 'release' : 'releases'}
          </div>
        </Tile>
      </div>

      <AttentionList items={attention} onTabChange={onTabChange} />

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <button
          type="button"
          onClick={() => onTabChange('dapps')}
          className={actionLinkClassName}
        >
          All dapps →
        </button>
        {canManagePublisher && (
          <>
            <button
              type="button"
              onClick={() => onTabChange('members')}
              className={actionLinkClassName}
            >
              Members →
            </button>
            <button
              type="button"
              onClick={() => onTabChange('kyb')}
              className={actionLinkClassName}
            >
              KYB details →
            </button>
          </>
        )}
      </div>
    </Section>
  );
}

function RecentDappsCard({
  dapps,
  onTabChange,
}: {
  dapps: SuperPublisherDetailData['dapps'];
  onTabChange: SuperPublisherTabProps['onTabChange'];
}) {
  const recent = sortDappsByActivity(dapps).slice(0, RECENT_DAPPS);

  return (
    <Section
      title="Dapps"
      trailing={
        <span className="text-xs tabular-nums text-neutral-500">
          {dapps.length}
        </span>
      }
    >
      {recent.length === 0 ? (
        <EmptyState message="This publisher has no dapps yet" />
      ) : (
        <ul className="divide-y divide-white/5">
          {recent.map((dapp) => (
            <PublisherDappRow key={dapp.id} dapp={dapp} />
          ))}
        </ul>
      )}
      {dapps.length > recent.length && (
        <div className="mt-4 border-t border-white/5 pt-4 text-xs">
          <button
            type="button"
            onClick={() => onTabChange('dapps')}
            className={actionLinkClassName}
          >
            All {dapps.length} dapps →
          </button>
        </div>
      )}
    </Section>
  );
}

function ProfileCard({
  publisher,
  formatDate,
  canManagePublisher,
  onEditProfile,
}: {
  publisher: SuperPublisherDetailData['publisher'];
  formatDate: SuperPublisherTabProps['formatDate'];
  canManagePublisher: boolean;
  onEditProfile: () => void;
}) {
  const { organization, individual } = publisher;
  const website = publisherWebsite(publisher);
  const entityCreated = organization?.created ?? individual?.created ?? null;

  return (
    <Section
      title="Profile"
      trailing={
        canManagePublisher && (
          <button
            type="button"
            onClick={onEditProfile}
            className={actionLinkClassName}
          >
            Edit →
          </button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Type">
          <Chip>{publisher.type}</Chip>
        </Field>
        <Field label="Website">
          {website ? (
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              title={website}
              className={`${LINK_CLASS} block truncate`}
            >
              {hostnameOf(website)}
            </a>
          ) : (
            <span className="text-neutral-600">—</span>
          )}
        </Field>
        {organization?.address && (
          <Field label="Address">
            <span className="text-neutral-300">{organization.address}</span>
          </Field>
        )}
        <Field label="Membership ID">
          <CopyableValue value={publisher.id} />
        </Field>
        {organization && (
          <Field label="Organization ID">
            <CopyableValue value={organization.id} />
          </Field>
        )}
        {individual && (
          <Field label="Individual ID">
            <CopyableValue value={individual.id} />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Profile created">
            <span className="text-xs text-neutral-300">
              {formatDate(entityCreated)}
            </span>
          </Field>
          <Field label="Joined">
            <span className="text-xs text-neutral-300">
              {formatDate(publisher.acceptedAt ?? publisher.created)}
            </span>
          </Field>
        </div>
      </div>
    </Section>
  );
}

function AccountCard({
  user,
  formatDate,
}: {
  user: SuperPublisherDetailData['publisher']['user'];
  formatDate: SuperPublisherTabProps['formatDate'];
}) {
  return (
    <Section
      title="Account"
      trailing={<TypedStatusBadge type="role" status={user.role} />}
    >
      <div className="flex flex-col gap-4">
        <Field label="Email">
          <CopyableValue value={user.email} mono={false} />
        </Field>
        <Field label="Full name">
          <span className="text-neutral-300">{user.fullName || '—'}</span>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email verified">
            <Flag on={user.emailVerified}>
              {user.emailVerified ? 'Verified' : 'Unverified'}
            </Flag>
          </Field>
          <Field label="Onboarding">
            <span className="text-neutral-300">{user.onboardingState}</span>
          </Field>
        </div>
        <Field label="Account created">
          <span className="text-xs text-neutral-300">
            {formatDate(user.created)}
          </span>
        </Field>
      </div>
    </Section>
  );
}

function OnChainCard({
  publisher,
}: {
  publisher: SuperPublisherDetailData['publisher'];
}) {
  if (
    !publisher.walletAddress &&
    !publisher.nftMintAddress &&
    !publisher.nftTransactionSignature
  ) {
    return null;
  }

  const cluster = publisher.nftCluster;

  return (
    <Section title="On-chain" trailing={<Chip>{cluster ?? 'mainnet'}</Chip>}>
      <div className="flex flex-col gap-4">
        {publisher.walletAddress && (
          <Field label="Wallet">
            <CopyableValue
              value={publisher.walletAddress}
              href={explorerUrl(`address/${publisher.walletAddress}`, cluster)}
            />
          </Field>
        )}
        {publisher.nftMintAddress && (
          <Field label="Publisher NFT mint">
            <CopyableValue
              value={publisher.nftMintAddress}
              href={explorerUrl(`address/${publisher.nftMintAddress}`, cluster)}
            />
          </Field>
        )}
        {publisher.nftTransactionSignature && (
          <Field label="Mint transaction">
            <CopyableValue
              value={publisher.nftTransactionSignature}
              href={explorerUrl(
                `tx/${publisher.nftTransactionSignature}`,
                cluster,
              )}
            />
          </Field>
        )}
        {publisher.nftMetadataUri && (
          <Field label="Metadata">
            <CopyableValue
              value={publisher.nftMetadataUri}
              href={publisher.nftMetadataUri}
            />
          </Field>
        )}
      </div>
    </Section>
  );
}
